// Firebase functions for admin operations
import { 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functionsInstance, app } from "./firebase-config";
import firebaseConfig from "./firebase-config";

// Delete a user account and all associated data
export const deleteUserAccount = async (userId) => {
  try {
    // Create the callable function reference
    const deleteUserFn = httpsCallable(functionsInstance, 'deleteUser');
    
    console.log(`Calling deleteUser function for user ${userId}`);
    console.log("Using Firebase project:", firebaseConfig.projectId);
    console.log("Using region: us-central1");
    
    // Call the Cloud Function
    const result = await deleteUserFn({ userId });
    
    console.log('User deletion result:', result.data);
    return { 
      success: true, 
      data: result.data 
    };
  } catch (error) {
    console.error('Error deleting user account:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Archive a user account (instead of deleting it)
export const archiveUserAccount = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'Missing user ID' };
    }
    
    // Create the callable function reference
    const archiveUserFn = httpsCallable(functionsInstance, 'archiveUser');
    
    console.log(`Calling archiveUser function for user ${userId}`);
    console.log("Using Firebase project:", firebaseConfig.projectId);
    console.log("Using region: us-central1");
    
    // Call the Cloud Function
    const result = await archiveUserFn({ userId });
    
    console.log('User archive result:', result.data);
    return { 
      success: true, 
      data: result.data 
    };
  } catch (error) {
    console.error('Error archiving user account:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Unarchive a user account
export const unarchiveUserAccount = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'Missing user ID' };
    }
    
    // Create the callable function reference
    const unarchiveUserFn = httpsCallable(functionsInstance, 'unarchiveUser');
    
    console.log(`Calling unarchiveUser function for user ${userId}`);
    console.log("Using Firebase project:", firebaseConfig.projectId);
    console.log("Using region: us-central1");
    
    // Call the Cloud Function
    const result = await unarchiveUserFn({ userId });
    
    console.log('User unarchive result:', result.data);
    return { 
      success: true, 
      data: result.data 
    };
  } catch (error) {
    console.error('Error unarchiving user account:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Hide a user from the network directory (admin only)
 * @param {string} userId - ID of the user to hide
 * @param {string} adminEmail - Email of the admin performing the action
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const hideUser = async (userId, adminEmail) => {
  try {
    console.log(`Admin ${adminEmail} hiding user ${userId}`);
    
    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const updateData = {
      isHidden: true,
      hiddenAt: serverTimestamp(),
      hiddenBy: adminEmail,
      lastUpdated: serverTimestamp()
    };
    
    await updateDoc(userRef, updateData);
    
    console.log(`User ${userId} hidden successfully`);
    return { success: true };
  } catch (error) {
    console.error('Error hiding user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Unhide a user from the network directory (admin only)
 * @param {string} userId - ID of the user to unhide
 * @param {string} adminEmail - Email of the admin performing the action
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const unhideUser = async (userId, adminEmail) => {
  try {
    console.log(`Admin ${adminEmail} unhiding user ${userId}`);
    
    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const updateData = {
      isHidden: false,
      unhiddenAt: serverTimestamp(),
      unhiddenBy: adminEmail,
      lastUpdated: serverTimestamp()
    };
    
    await updateDoc(userRef, updateData);
    
    console.log(`User ${userId} unhidden successfully`);
    return { success: true };
  } catch (error) {
    console.error('Error unhiding user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get hidden users (admin only)
 * @returns {Promise<{success: boolean, data: UserProfile[], error?: string}>}
 */
export const getHiddenUsers = async () => {
  try {
    console.log('Fetching hidden users...');
    
    const usersRef = collection(db, 'user_profiles');
    const q = query(
      usersRef,
      where('isHidden', '==', true),
      orderBy('hiddenAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    const hiddenUsers = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      hiddenUsers.push({
        id: doc.id,
        uid: doc.id,
        ...userData
      });
    });
    
    console.log(`Retrieved ${hiddenUsers.length} hidden users`);
    
    return { success: true, data: hiddenUsers };
  } catch (error) {
    console.error('Error fetching hidden users:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Update user status (admin only)
 * Allowed values: 'waitlist', 'invited', 'active'
 * Handles legacy users by creating user_profiles document if it doesn't exist
 * @param {string} userId - ID of the user to update
 * @param {string} newStatus - New status value
 * @param {string} adminEmail - Email of the admin performing the action
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateUserStatus = async (userId, newStatus, adminEmail) => {
  try {
    // Validate status
    const validStatuses = ['waitlist', 'invited', 'active'];
    if (!validStatuses.includes(newStatus)) {
      return { success: false, error: `Invalid status. Must be one of: ${validStatuses.join(', ')}` };
    }

    console.log(`Admin ${adminEmail} updating user ${userId} status to: ${newStatus}`);
    
    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);
    
    // If user_profiles document doesn't exist, this is a legacy user
    // Try to create the document with data from waitlist collection
    if (!userSnap.exists()) {
      console.log(`User profile not found for ${userId}. Checking waitlist data for legacy user...`);
      
      // Query waitlist collection for user data
      const waitlistRef = collection(db, 'waitlist');
      const q = query(waitlistRef, where('userId', '==', userId));
      const waitlistSnapshot = await getDocs(q);
      
      if (waitlistSnapshot.empty) {
        console.log(`No waitlist data found for user ${userId}`);
        return { success: false, error: 'User not found in user_profiles or waitlist collections' };
      }
      
      // Get waitlist data
      const waitlistData = waitlistSnapshot.docs[0].data();
      const waitlistDocId = waitlistSnapshot.docs[0].id;
      console.log(`Found waitlist data for legacy user ${userId}, creating user_profiles document...`);
      
      // Build basic user profile from waitlist data
      const firstName = waitlistData.userInfo?.firstName || waitlistData.firstName || '';
      const lastName = waitlistData.userInfo?.lastName || waitlistData.lastName || '';
      const email = waitlistData.userInfo?.email || waitlistData.email || '';
      const linkedinUrl = waitlistData.userInfo?.linkedinUrl || waitlistData.linkedinUrl || '';
      
      // Create user_profiles document with available data
      const newUserProfile = {
        uid: userId, // Firebase Auth UID
        userId: userId, // Consistent with standard pattern
        email: email,
        displayName: firstName && lastName ? `${firstName} ${lastName}`.trim() : email.split('@')[0],
        basicInfo: {
          email: email,
          firstName: firstName,
          lastName: lastName,
          name: firstName && lastName ? `${firstName} ${lastName}`.trim() : ''
        },
        linkedinUrl: linkedinUrl,
        createdAt: waitlistData.created_at || serverTimestamp(),
        waitlistSubmissionId: waitlistDocId,
        status: newStatus,
        statusUpdatedAt: serverTimestamp(),
        statusUpdatedBy: adminEmail,
        lastUpdated: serverTimestamp(),
        // Mark as migrated from legacy
        migratedFromWaitlist: true,
        migratedAt: serverTimestamp()
      };
      
      // Create the document
      await setDoc(userRef, newUserProfile);
      console.log(`Created user_profiles document for legacy user ${userId}`);
      console.log(`User ${userId} status set to ${newStatus} successfully`);
      
      return { success: true };
    }
    
    // User profile exists, just update the status
    const updateData = {
      status: newStatus,
      statusUpdatedAt: serverTimestamp(),
      statusUpdatedBy: adminEmail,
      lastUpdated: serverTimestamp()
    };
    
    await updateDoc(userRef, updateData);
    
    console.log(`User ${userId} status updated to ${newStatus} successfully`);
    return { success: true };
  } catch (error) {
    console.error('Error updating user status:', error);
    return { success: false, error: error.message };
  }
};